/**
 * The demo's "both sides at once" shell (SKE-4166, phase 2 + UX uplift).
 *
 * Wraps — never edits — the real approver UI. `App.tsx` / `AppMobile.tsx` are
 * shipped to genuine reviewers verbatim; this component composes around them,
 * so there is exactly one approver implementation and the demo cannot drift
 * from the product it is advertising.
 *
 * Outside demo mode it renders its children and nothing else, which is what
 * keeps `collab.skedsocial.com/#/review/{slug}` untouched.
 *
 * DESKTOP (this file), at >= 1200px
 *   A genuine two-pane. The approver `App` sits inside a browser-window
 *   ClientFrame ("what your client sees"); the marketer's mock Sked panel sits
 *   beside it inside a "Your Sked" TeamChrome. Both are permanently on screen —
 *   there is no rail, no edge tab, no auto-reveal to miss.
 *
 *   The two-pane fits at 1200 because the demo hands `App` a COMPACT prop: the
 *   approver drops to a two-column grid (list + preview) and folds its Post
 *   History & Discussion column into a drawer, so the client area needs ~800px,
 *   the team chrome ~340px, and the pair clears 1200 without the approver grid
 *   picking up a horizontal scrollbar.
 *
 *   The 1200 boundary is owned by Homepage's media query (one source of truth,
 *   exported here as DEMO_TWO_PANE_MIN_WIDTH). Below it, `isMobile` is true and
 *   this shell is never asked to render the two-pane — the mobile flip is used
 *   instead.
 *
 * NO TRANSFORM ON ANY ANCESTOR OF `App`
 *   The approver renders a `position: fixed` confetti canvas; a transform on an
 *   ancestor would re-anchor it (see demoReveal.ts). The two-pane is flexbox +
 *   borders/shadow only, and the board's load reveal is an opacity fade.
 *
 * MOBILE
 *   Deliberately NOT this layout. See DemoMobileMirror.tsx.
 */

import { useEffect } from 'react'

import ClientFrame from './ClientFrame'
import DemoApproveHint from './DemoApproveHint'
import DemoFlyLayer from './DemoFlyLayer'
import DemoMobileMirror from './DemoMobileMirror'
import { trackDemoTeaserViewed } from './demoAnalytics'
import { isDemoMode } from './demoMode'
import { DEMO_REVEAL_FADE, demoRevealStyle } from './demoReveal'
import MarketerMirror from './MarketerMirror'
import TeamChrome from './TeamChrome'
import { useDemoMirror } from './useDemoMirror'

/**
 * The viewport at which the desktop two-pane engages. Homepage reads this to
 * decide `isMobile`, so the boundary lives in exactly one place: below it the
 * mobile flip renders, at or above it this two-pane does.
 */
export const DEMO_TWO_PANE_MIN_WIDTH = 1200

/** Width of the docked "Your Sked" team pane. */
const TEAM_CHROME_WIDTH_CLASS = 'w-[340px]'

const DesktopDualView: React.FC<React.PropsWithChildren> = ({ children }) => {
  const mirror = useDemoMirror()

  // The "both sides at once" beat. In the two-pane it is on screen from first
  // paint, so it is counted once on mount. The guard inside
  // `trackDemoTeaserViewed` keeps the mobile shell from double-counting.
  useEffect(() => {
    trackDemoTeaserViewed({ surface: 'desktop-two-pane', trigger: 'auto' })
  }, [])

  return (
    <div className="flex h-screen w-full items-stretch gap-3 overflow-hidden bg-[#fcfcfc] p-3">
      {/* The client's screen. Step 0 of the load reveal — a fade with no
          transform, deliberately; see the containing-block note in
          demoReveal.ts. */}
      <div
        data-tour="client"
        style={demoRevealStyle(0)}
        className={`min-w-0 flex-1 ${DEMO_REVEAL_FADE}`}
      >
        <ClientFrame>{children}</ClientFrame>
      </div>

      {/* The team's screen, docked permanently beside it. */}
      <div
        data-tour="team"
        className={`${TEAM_CHROME_WIDTH_CLASS} h-full shrink-0`}
      >
        <TeamChrome>
          <MarketerMirror {...mirror} />
        </TeamChrome>
      </div>

      {/* Approve → team ghost-clone fly. Portals to body; only spawns on a
          visitor action, so it costs nothing at rest. */}
      <DemoFlyLayer />

      {/* Lingering "approve a post" cue. Portals to body; self-gates on the
          tour + approved-once flags, so it costs nothing once the visitor acts. */}
      <DemoApproveHint />
    </div>
  )
}

export interface DemoDualViewProps {
  /** Homepage resolves this from its media query (1200 in demo, 906 outside). */
  isMobile?: boolean
  children: React.ReactNode
}

const DemoDualView: React.FC<DemoDualViewProps> = ({
  isMobile = false,
  children,
}) => {
  // Not a hook, and the demo/reviewer distinction is fixed for a page load, so
  // this early return can't change hook order between renders.
  if (!isDemoMode()) {
    return <>{children}</>
  }

  return isMobile ? (
    <DemoMobileMirror>{children}</DemoMobileMirror>
  ) : (
    <DesktopDualView>{children}</DesktopDualView>
  )
}

export default DemoDualView
